import { Box, Typography } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import type { GridApiPro } from '@mui/x-data-grid-pro';
import type { GridApiCommunity } from '@mui/x-data-grid/internals';
import { caseInsensitiveIncludes, now } from '@vy/utils';
import dayjs from 'dayjs';
import type { MutableRefObject } from 'react';
import { useCallback, useMemo } from 'react';
import type { FilterLineType } from '../../../components/new-design/vyTableFilter';
import { useFailedInvoiceDeliveriesReport } from '../../../hooks/requests/use-failed-invoice-deliveries-report';
import { useSearchWithSearchParams } from '../../../hooks/use-search-with-search-params';
import { formatDate } from '../../../utils/date';
import { ReportTable } from '../reports-components/ReportTable';
import { REPORT_TYPES } from '../reports-page/reports-page-types/report-type';
import { FailedInvoiceDeliveriesReportTable } from './failed-invoice-deliveries-report-components/FailedInvoiceDeliveriesReportTable';
import { FailedInvoiceDeliveriesReportToolbar } from './failed-invoice-deliveries-report-components/FailedInvoiceDeliveriesReportToolbar';
import { useFailedDeliveriesFilterFields } from './failed-invoice-deliveries-report-components/use-failed-deliveries-filter-fields';
import { applyFailedDeliveriesFilters, useFailedDeliveriesFilters } from './failed-invoice-deliveries-report-components/use-failed-deliveries-filters';

type FailedInvoiceDeliveriesReportContentProps = {
  reportId?: string;
};

export const FailedInvoiceDeliveriesReportContent: React.FC<FailedInvoiceDeliveriesReportContentProps> = ({
  reportId,
}) => {
  const { data: failedInvoiceDeliveriesReportData, isLoading } = useFailedInvoiceDeliveriesReport(reportId);
  const [searchText, setSearchText] = useSearchWithSearchParams();
  const { report, creationDate } = failedInvoiceDeliveriesReportData;
  const apiRef = useGridApiRef();
  const allRows = report.reportItems;

  const { filters, handleFilterChange } = useFailedDeliveriesFilters();
  const filterFields = useFailedDeliveriesFilterFields(allRows);

  const handleFiltersChange = useCallback(
    (nextFilters: FilterLineType[]) => handleFilterChange(nextFilters),
    [handleFilterChange],
  );

  const filteredRows = useMemo(() => {
    let items = applyFailedDeliveriesFilters(allRows, filters);

    const query = searchText?.toLowerCase() ?? '';
    if (query) {
      items = items.filter((item) => caseInsensitiveIncludes(item.customer.name, query));
    }

    return items;
  }, [allRows, filters, searchText]);

  const handleExport = () => {
    const api = apiRef.current;
    if (!api) return;

    api.exportDataAsCsv({
      fileName: `Email Exception Report as of ${formatDate(creationDate)} (issued ${formatDate(now())})`,
    });
  };

  const reportGeneratedAtDate = creationDate ? dayjs(creationDate) : null;

  const lastRefreshedLabel = reportGeneratedAtDate ? (
    <Typography variant="caption" sx={{
      color: "text.secondary"
    }}>
      Last updated: {reportGeneratedAtDate.format('MMM D, YYYY [at] h:mm A')}
    </Typography>
  ) : null;

  return (
    <>
      <Box
        sx={{
          width: '100%',
          height: 30,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mb: 1,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 500,
            fontSize: "20px"
          }}>
          Email Exception Report
        </Typography>
        {lastRefreshedLabel}
      </Box>
      <ReportTable
        title="Email Exception Report"
        hideTitle
        exportCsvFileName="Email Exception Report"
        reportCreationDate={creationDate}
        reportType={REPORT_TYPES.FAILED_INVOICE_DELIVERIES}
        apiRef={apiRef as MutableRefObject<GridApiCommunity>}
        onExportCsv={handleExport}
        hideMenu
      >
        <FailedInvoiceDeliveriesReportToolbar
          onSearchChange={(value) => setSearchText(value)}
          filterFields={filterFields}
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onExport={handleExport}
        />

        <FailedInvoiceDeliveriesReportTable
          apiRef={apiRef as MutableRefObject<GridApiPro>}
          rows={filteredRows}
          isLoading={isLoading}
        />
      </ReportTable>
    </>
  );
};
